import PropTypes from "prop-types";
import React, { PureComponent } from "react";

import { getScreenPointerPositionFromEvent } from "../../utils/mouseUtils";

// import debugUtils from "../../utils/debugUtils";

const LISTENER_ID = "ContextBarLens";

const PADDING_EACH_SIZE = 20;

const mousePosWithinParent = (e, parentLeft) => {
  const x = getScreenPointerPositionFromEvent(e).x;
  return x - parentLeft;
};

const calcLensLeft = state => (state.mainViewLeftTs - state.scrollLeftTs) / state.unitScale;
const calcLensRight = state => (state.mainViewRightTs - state.scrollLeftTs) / state.unitScale;
const calcLensWidth = state => calcLensRight(state) - calcLensLeft(state);

class ContextBarLens extends PureComponent {
  constructor(props, context) {
    super(props, context);
    this.scrollUpdateReceived = this.scrollUpdateReceived.bind(this);
    this.onLensMouseDown = this.onLensMouseDown.bind(this);
    this.onLensMouseMove = this.onLensMouseMove.bind(this);
    this.onLensMouseUp = this.onLensMouseUp.bind(this);
    this.onResizeMouseDown = this.onResizeMouseDown.bind(this);
    this.onResizeMouseMove = this.onResizeMouseMove.bind(this);
    this.onResizeMouseUp = this.onResizeMouseUp.bind(this);
    this.onLeftResizeMouseDown = this.onLeftResizeMouseDown.bind(this);
    this.onRightResizeMouseDown = this.onRightResizeMouseDown.bind(this);

    this._parentLeftOnMousedown = null;
  }

  componentDidMount() {
    this.props.registerScrollListener(LISTENER_ID, this.scrollUpdateReceived);
  }

  // componentWillReceiveProps(nextProps) {
  //   debugUtils.logPropChanges("ContextBarLens", this.props, nextProps, true);
  // }

  componentWillUnmount() {
    this.props.unregisterScrollListener(LISTENER_ID);
  }

  // Note: This function is controlled by registering against ContextBarView
  // It does not go all the way to the scroll view, it is correctly localized
  scrollUpdateReceived(scrollState) {
    const lensLeft = calcLensLeft(scrollState);
    const lensWidth = calcLensWidth(scrollState);
    this.applyTransform(lensLeft, lensWidth);
  }

  onLensMouseDown(e) {
    document.addEventListener("mousemove", this.onLensMouseMove);
    document.addEventListener("mouseup", this.onLensMouseUp);
    document.addEventListener("touchmove", this.onLensMouseMove);
    document.addEventListener("touchend", this.onLensMouseUp);

    this._parentLeftOnMousedown = this.props.requestParentLeft();

    this.props.onDragStart(mousePosWithinParent(e, this._parentLeftOnMousedown));
  }

  onLensMouseMove(e) {
    this.props.onDrag(mousePosWithinParent(e, this._parentLeftOnMousedown));
  }

  onLensMouseUp(_e) {
    document.removeEventListener("mousemove", this.onLensMouseMove);
    document.removeEventListener("mouseup", this.onLensMouseUp);
    document.removeEventListener("touchmove", this.onLensMouseMove);
    document.removeEventListener("touchend", this.onLensMouseUp);
    this.props.onDragEnd();
  }

  onLeftResizeMouseDown(e) {
    this.onResizeMouseDown(e, true);
  }

  onRightResizeMouseDown(e) {
    this.onResizeMouseDown(e, false);
  }

  onResizeMouseDown(e, fromLeft) {
    document.addEventListener("mousemove", this.onResizeMouseMove);
    document.addEventListener("mouseup", this.onResizeMouseUp);
    document.addEventListener("touchmove", this.onResizeMouseMove);
    document.addEventListener("touchend", this.onResizeMouseUp);

    this._parentLeftOnMousedown = this.props.requestParentLeft();

    this.props.onResizeDragStart(mousePosWithinParent(e, this._parentLeftOnMousedown), fromLeft);
    // Don't let it bubble through to rectangle for a lens drag at the same time
    e.stopPropagation();
  }

  onResizeMouseMove(e) {
    this.props.onResizeDrag(mousePosWithinParent(e, this._parentLeftOnMousedown));
  }

  onResizeMouseUp(_e) {
    document.removeEventListener("mousemove", this.onResizeMouseMove);
    document.removeEventListener("mouseup", this.onResizeMouseUp);
    document.removeEventListener("touchmove", this.onResizeMouseMove);
    document.removeEventListener("touchend", this.onResizeMouseUp);
    this.props.onResizeDragEnd();
  }

  applyTransform(lensLeft, lensWidth) {
    const totalWidth = lensWidth + PADDING_EACH_SIZE * 2;
    const finalLeft = lensLeft - PADDING_EACH_SIZE;
    this._domNode.style.transform = `translateX(${finalLeft}px)`;
    this._domNode.style.width = `${totalWidth}px`;
    this._rectNode.style.width = `${lensWidth}px`;
  }

  renderTooltip() {
    return <div className="contextLensRectTooltip" ref={this.props.innerRef} />;
  }

  render() {
    const totalWidth = this.props.width + PADDING_EACH_SIZE * 2;
    const finalLeft = this.props.left - PADDING_EACH_SIZE;
    if (this._domNode) {
      this.applyTransform(this.props.left, this.props.width);
    }

    return (
      <div
        className="contextLens"
        ref={node => (this._domNode = node)}
        style={{
          width: `${totalWidth}px`,
          transform: `translateX(${finalLeft}px)`
        }}
      >
        <div
          className="contextLensRect"
          style={{
            width: `${this.props.width}px`
          }}
          ref={node => (this._rectNode = node)}
          onMouseDown={this.onLensMouseDown}
          onTouchStart={this.onLensMouseDown}
        >
          {this.renderTooltip()}
        </div>
        <div
          className="contextLensLeftArrow"
          onMouseDown={this.onLeftResizeMouseDown}
          onTouchStart={this.onLeftResizeMouseDown}
        >
          <svg className="icon iconMono">
            <use xlinkHref="#contextLensLeft" />
          </svg>
        </div>
        <div
          className="contextLensRightArrow"
          onMouseDown={this.onRightResizeMouseDown}
          onTouchStart={this.onRightResizeMouseDown}
        >
          <svg className="icon iconMono">
            <use xlinkHref="#contextLensRight" />
          </svg>
        </div>
      </div>
    );
  }
}

ContextBarLens.propTypes = {
  width: PropTypes.number.isRequired,
  left: PropTypes.number.isRequired,

  onDragStart: PropTypes.func.isRequired,
  onDrag: PropTypes.func.isRequired,
  onDragEnd: PropTypes.func.isRequired,
  onResizeDragStart: PropTypes.func.isRequired,
  onResizeDrag: PropTypes.func.isRequired,
  onResizeDragEnd: PropTypes.func.isRequired,
  requestParentLeft: PropTypes.func.isRequired,

  registerScrollListener: PropTypes.func.isRequired,
  unregisterScrollListener: PropTypes.func.isRequired,

  leftDateString: PropTypes.string,
  rightDateString: PropTypes.string,
  innerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })])
};

const exportedView = React.forwardRef((props, ref) => <ContextBarLens innerRef={ref} {...props} />);
export default exportedView;
